import { useState } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import { useDeals } from '../hooks/useDeals';
import { useText } from '../context/TextContext';
import { Deal } from '../models/shared/Deal';

interface DealPickerProps {
    value?: string;
    onChange: (dealId: string) => void;
    onBlur?: () => void;
    placeholder?: string;
    className?: string;
    error?: boolean;
    disabled?: boolean;
}

export default function DealPicker({
    value,
    onChange,
    onBlur,
    placeholder,
    className,
    error,
    disabled
}: DealPickerProps) {
    const { allDeals } = useDeals();
    const text = useText();
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [activeIx, setActiveIx] = useState(0);

    const selectedDeal = allDeals.find(deal => deal.id === value);
    const filteredDeals = allDeals.filter(deal =>
        deal.name.toLowerCase().includes(query.toLowerCase())
    );

    const select = (deal: Deal) => {
        onChange(deal.id || '');
        setOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIx(ix => Math.min(ix + 1, filteredDeals.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIx(ix => Math.max(ix - 1, 0));
        } else if (e.key === 'Enter' && open && filteredDeals[activeIx]) {
            e.preventDefault();
            select(filteredDeals[activeIx]);
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    const inputClassName = error ? 'is-invalid' : value ? 'is-valid' : '';

    return (
        <div className="position-relative">
            <Form.Control
                value={open ? query : selectedDeal?.name || ''}
                onChange={(e) => { setQuery(e.target.value); setActiveIx(0); setOpen(true); }}
                onFocus={() => { setQuery(''); setActiveIx(0); setOpen(true); }}
                onBlur={() => { setOpen(false); onBlur?.(); }}
                onKeyDown={handleKeyDown}
                placeholder={open ? selectedDeal?.name : placeholder}
                className={`${inputClassName} ${className || ''}`.trim()}
                disabled={disabled}
            />

            {open && (
                <Dropdown.Menu
                    show
                    className="w-100 mt-1 p-0 shadow border overflow-auto"
                    style={{ maxHeight: 300 }}
                    onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                >
                    {filteredDeals.map((deal, ix) => (
                        <Dropdown.Item
                            key={deal.id}
                            active={ix === activeIx}
                            onClick={() => select(deal)}
                        >
                            <div>{deal.name}</div>
                            {deal.place?.formattedAddress && <div className="small text-muted text-truncate">{deal.place.formattedAddress}</div>}
                        </Dropdown.Item>
                    ))}
                    {filteredDeals.length === 0 && <Dropdown.Item disabled>{text.noResults}</Dropdown.Item>}
                </Dropdown.Menu>
            )}
        </div>
    );
}
